import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AdminAlert } from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import {
  BrandSiteApiError,
  upsertBrandSiteCollection,
  type BrandSiteCollection,
} from "@/lib/brand-site-api";
import {
  normalizeSchemaSettings,
  schemaSettingsToEditorRows,
  upsertSchemaInTemplate,
} from "@/lib/brand-site-item-schema";

const FIELD_TYPES = [
  { value: "text", label: "文本" },
  { value: "html", label: "正文 HTML" },
  { value: "image", label: "图片" },
  { value: "textarea", label: "多行文本" },
] as const;

type SchemaRow = { id: string; type: string; label: string };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string | number;
  brandSiteId: number;
  collection: BrandSiteCollection | null;
  designBase: string;
  onSaved: (collection: BrandSiteCollection) => void;
};

/** 栏目设置：显示名、字段表、列表/单篇 Liquid 模板 */
export function BrandSiteCollectionSettingsSheet({
  open,
  onOpenChange,
  accountId,
  brandSiteId,
  collection,
  designBase,
  onSaved,
}: Props) {
  const [navLabel, setNavLabel] = useState("");
  const [listHtml, setListHtml] = useState("");
  const [itemHtml, setItemHtml] = useState("");
  const [rows, setRows] = useState<SchemaRow[]>([]);
  const [schemaName, setSchemaName] = useState("文章");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"fields" | "list" | "item">("fields");

  useEffect(() => {
    if (!collection || !open) return;
    setNavLabel(collection.nav_label);
    setListHtml(collection.list_template_html);
    setItemHtml(collection.item_template_html);
    const { schemaName: nextSchemaName, rows: nextRows } = schemaSettingsToEditorRows(collection);
    setSchemaName(nextSchemaName);
    setRows(nextRows);
    setError(null);
    setTab("fields");
  }, [collection, open]);

  const listPreviewPath = collection ? `${collection.key}*` : "";
  const itemPreviewPath = collection ? `${collection.key}/:slug` : "";

  const canSave = useMemo(() => navLabel.trim().length > 0 && Boolean(collection), [collection, navLabel]);

  async function handleSave() {
    if (!collection) return;
    setSaving(true);
    setError(null);
    try {
      const settings = normalizeSchemaSettings(rows);
      const nextItemHtml = upsertSchemaInTemplate(itemHtml, {
        name: schemaName,
        settings,
      });
      const payload = await upsertBrandSiteCollection(accountId, brandSiteId, {
        key: collection.key,
        nav_label: navLabel.trim(),
        list_template_html: listHtml,
        item_template_html: nextItemHtml,
      });
      onSaved(payload.collection);
      toast.success("栏目已保存（草稿）；正式站请到「发布」上线");
      onOpenChange(false);
    } catch (caught) {
      const message =
        caught instanceof BrandSiteApiError
          ? caught.message
          : caught instanceof Error && caught.message
            ? caught.message
            : "保存失败，请稍后重试";
      setError(message);
      // 面板内已有错误条，不再叠 toast，避免同一句说两遍
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-hidden sm:max-w-xl md:max-w-2xl"
      >
        <SheetHeader className="shrink-0 border-b border-border text-left">
          <SheetTitle>栏目设置</SheetTitle>
          <SheetDescription>
            {collection ? (
              <span className="font-mono">/{collection.key}</span>
            ) : (
              "配置显示名、文章字段与 Liquid 模板"
            )}
          </SheetDescription>
        </SheetHeader>

        {!collection ? (
          <p className="p-4 text-sm text-muted-foreground">未选择栏目</p>
        ) : (
          <>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
              {error ? <AdminAlert>{error}</AdminAlert> : null}

              <div className="space-y-1.5">
                <Label htmlFor="collection-nav-label">显示名</Label>
                <Input
                  id="collection-nav-label"
                  value={navLabel}
                  onChange={(event) => setNavLabel(event.target.value)}
                  autoComplete="off"
                />
              </div>

              <div
                className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5"
                role="tablist"
                aria-label="栏目设置分区"
              >
                {(
                  [
                    { id: "fields" as const, label: "字段" },
                    { id: "list" as const, label: "列表模板" },
                    { id: "item" as const, label: "单篇模板" },
                  ] as const
                ).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={tab === item.id}
                    className={
                      tab === item.id
                        ? "rounded-md bg-background px-2.5 py-1 text-xs font-medium shadow-sm"
                        : "rounded-md px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground"
                    }
                    onClick={() => setTab(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {tab === "fields" ? (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    字段会写入单篇模板的 {"{% schema %}"}，决定文章编辑表单有哪些项。
                  </p>
                  <div className="space-y-1.5">
                    <Label htmlFor="schema-name">字段组名称</Label>
                    <Input
                      id="schema-name"
                      value={schemaName}
                      onChange={(event) => setSchemaName(event.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <ul className="space-y-2">
                    {rows.map((row, index) => (
                      <li
                        key={`${row.id}-${index}`}
                        className="grid grid-cols-[1fr_1fr_7rem_auto] gap-2 rounded-lg border border-border p-2"
                      >
                        <Input
                          value={row.id}
                          placeholder="英文键名"
                          aria-label="字段英文键名"
                          autoComplete="off"
                          name={`collection-field-key-${index}`}
                          className="font-mono text-xs"
                          onChange={(event) => {
                            const next = event.target.value;
                            setRows((current) =>
                              current.map((item, i) =>
                                i === index ? { ...item, id: next } : item,
                              ),
                            );
                          }}
                        />
                        <Input
                          value={row.label}
                          placeholder="标签"
                          aria-label="字段标签"
                          autoComplete="off"
                          name={`collection-field-label-${index}`}
                          onChange={(event) => {
                            const next = event.target.value;
                            setRows((current) =>
                              current.map((item, i) =>
                                i === index ? { ...item, label: next } : item,
                              ),
                            );
                          }}
                        />
                        <select
                          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                          value={row.type}
                          aria-label="字段类型"
                          onChange={(event) => {
                            const next = event.target.value;
                            setRows((current) =>
                              current.map((item, i) =>
                                i === index ? { ...item, type: next } : item,
                              ),
                            );
                          }}
                        >
                          {FIELD_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label="删除字段"
                          onClick={() =>
                            setRows((current) => current.filter((_, i) => i !== index))
                          }
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setRows((current) => [
                        ...current,
                        { id: `field_${current.length + 1}`, type: "text", label: "新字段" },
                      ])
                    }
                  >
                    <Plus className="size-3.5" />
                    添加字段
                  </Button>
                </div>
              ) : null}

              {tab === "list" ? (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label htmlFor="list-template">列表页 Liquid</Label>
                    <Link
                      to={`${designBase}/edit?page=${encodeURIComponent(listPreviewPath)}`}
                      className="text-xs text-primary-readable hover:underline"
                    >
                      在设计页预览 /{listPreviewPath}
                    </Link>
                  </div>
                  <Textarea
                    id="list-template"
                    value={listHtml}
                    onChange={(event) => setListHtml(event.target.value)}
                    rows={16}
                    className="min-h-[280px] font-mono text-xs"
                  />
                </div>
              ) : null}

              {tab === "item" ? (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label htmlFor="item-template">单篇页 Liquid</Label>
                    <Link
                      to={`${designBase}/edit?page=${encodeURIComponent(itemPreviewPath)}`}
                      className="text-xs text-primary-readable hover:underline"
                    >
                      在设计页预览 /{itemPreviewPath}
                    </Link>
                  </div>
                  <Textarea
                    id="item-template"
                    value={itemHtml}
                    onChange={(event) => setItemHtml(event.target.value)}
                    rows={16}
                    className="min-h-[280px] font-mono text-xs"
                  />
                  <p className="text-xs text-muted-foreground">
                    保存时会把「字段」写回本模板的 schema 块；若你手改了 schema，以字段表为准覆盖。
                  </p>
                </div>
              ) : null}
            </div>

            <div className="flex shrink-0 justify-end gap-2 border-t border-border p-4">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button type="button" disabled={!canSave || saving} onClick={() => void handleSave()}>
                {saving ? "保存中…" : "保存栏目"}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
